import { useState, useEffect } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { useUser } from '../../hooks/useUser'
import { format } from 'date-fns'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import StatisticsObjectChart from './StatisticsObjectChart'
import AddReadingModal from './AddReadingModal.tsx'
import ReadingsManagementTable from './ReadingsManagementTable'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

interface InfrastructureObject {
  id: number
  name: string
  infrastructure_type: string
  location?: string
  readings_count: number
  last_reading_at?: string
}

interface Reading {
  id?: number
  object_id?: number
  object_name: string
  infrastructure_type: string
  reading_type: string
  reading_type_label: string
  value?: number
  datetime?: string
  reading_datetime?: string
  date?: string  // For usage mode
  usage?: number // For usage mode
  user_name?: string
  notes?: string
}

interface ReadingTypeLabels {
  [key: string]: string
}

interface StatisticsPageProps {
  token: string
}

export default function StatisticsPage({ token }: StatisticsPageProps) {
  const { user } = useUser(token)
  const [objects, setObjects] = useState<InfrastructureObject[]>([])
  const [selectedObjects, setSelectedObjects] = useState<number[]>([])
  const [readings, setReadings] = useState<Reading[]>([])
  const [readingTypeLabels, setReadingTypeLabels] = useState<ReadingTypeLabels>({})
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date()
    date.setDate(date.getDate() - 30) // Domyślnie ostatnie 30 dni dla nowo dodanych odczytów
    return date
  })
  const [endDate, setEndDate] = useState<Date>(new Date())
  const [showAddModal, setShowAddModal] = useState(false)
  const [viewMode, setViewMode] = useState<'readings' | 'usage'>('readings')
  const { showSuccess } = useNotificationContext()

  const isMasterAdmin = user?.role === 'MASTER_ADMIN'

  // Quick date range presets
  const datePresets = [
    { label: '3 dni', days: 3 },
    { label: '7 dni', days: 7 },
    { label: '30 dni', days: 30 },
  ]

  useEffect(() => {
    loadReadingTypes()
    loadObjectsAndReadings()
  }, [startDate, endDate])

  // React on viewMode change
  useEffect(() => {
    loadReadings()
  }, [viewMode])

  const loadReadingTypes = async () => {
    try {
      const response = await fetch('/api/statistics/reading-types', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setReadingTypeLabels(data.labels || {})
      }
    } catch (error) {
      console.error('Error loading reading types:', error)
    }
  }

  const loadObjectsAndReadings = async () => {
    setLoading(true)
    try {
      // Load objects with readings in the date range
      const objectsResponse = await fetch(
        `/api/statistics/objects?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      if (objectsResponse.ok) {
        const objectsData = await objectsResponse.json()
        setObjects(objectsData)
        // Auto-select all objects initially
        setSelectedObjects(objectsData.map((obj: InfrastructureObject) => obj.id))
      }

      // Load readings for selected objects
      await loadReadings()
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadReadings = async (objectIds?: number[]) => {
    const ids = objectIds || selectedObjects
    if (ids.length === 0) {
      setReadings([])
      return
    }

    try {
      const params = new URLSearchParams({
        start_date: startDate.toISOString().split('T')[0],  // Date only for date filters
        end_date: endDate.toISOString().split('T')[0],
      })

      // Include object_ids for individual readings mode only
      if (viewMode === 'readings') {
        ids.forEach(id => params.append('object_ids', id.toString()))
      } else {
        // For usage mode, include object_ids as before
        ids.forEach(id => params.append('object_ids', id.toString()))
      }

      const endpoint = viewMode === 'readings' ? '/api/statistics/readings' : '/api/statistics/usage'
      const response = await fetch(`${endpoint}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.ok) {
        const data = await response.json()
        setReadings(data)
      }
    } catch (error) {
      console.error('Error loading readings:', error)
    }
  }

  const handleDatePreset = (days: number) => {
    const newStartDate = new Date()
    newStartDate.setDate(newStartDate.getDate() - days)
    setStartDate(newStartDate)
    setEndDate(new Date())
  }

  const handleObjectToggle = (objectId: number) => {
    const newSelected = selectedObjects.includes(objectId)
      ? selectedObjects.filter(id => id !== objectId)
      : [...selectedObjects, objectId]

    setSelectedObjects(newSelected)
    loadReadings(newSelected)
  }

  const handleSelectAllObjects = () => {
    setSelectedObjects(objects.map(obj => obj.id))
    loadReadings(objects.map(obj => obj.id))
  }

  const handleDeselectAllObjects = () => {
    setSelectedObjects([])
    loadReadings([])
  }

  const handleReadingAdded = () => {
    setShowAddModal(false)
    loadObjectsAndReadings()
  }

  // Group readings by object
  const readingsByObject = readings.reduce((acc, reading) => {
    const objectId = reading.object_id
    if (objectId !== undefined && objectId !== null) {
      if (!acc[objectId]) {
        acc[objectId] = []
      }
      acc[objectId].push(reading)
    }
    return acc
  }, {} as { [key: number]: Reading[] })

  const selectedObjectsData = objects.filter(obj => selectedObjects.includes(obj.id))

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Ładowanie danych...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Statystyki</h2>
        <div className="flex space-x-2">
          <div className="flex bg-gray-100 rounded-md p-1">
            <button
              onClick={() => {
                console.log('Switching to readings mode')
                setViewMode('readings')
                loadReadings()
              }}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'readings'
                  ? 'bg-white text-gray-900 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Odczyty
            </button>
            <button
              onClick={() => {
                console.log('Switching to usage mode')
                setViewMode('usage')
                loadReadings()
              }}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'usage'
                  ? 'bg-white text-gray-900 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Zużycie ({viewMode === 'usage' ? 'AKTYWNY' : 'Kliknij aby zobaczyć różnice'})
            </button>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium flex items-center"
          >
            <span className="mr-2">+</span>
            Dodaj odczyt
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="space-y-4">
          {/* Quick date presets */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zakres czasu
            </label>
            <div className="flex gap-2">
              {datePresets.map(preset => (
                <button
                  key={preset.days}
                  onClick={() => handleDatePreset(preset.days)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    startDate.getTime() === new Date(Date.now() - preset.days * 24 * 60 * 60 * 1000).getTime()
                      ? 'bg-blue-100 text-blue-700 border-2 border-blue-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom date range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Od
              </label>
              <input
                type="datetime-local"
                value={format(startDate, "yyyy-MM-dd'T'HH:mm")}
                onChange={(e) => setStartDate(new Date(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Do
              </label>
              <input
                type="datetime-local"
                value={format(endDate, "yyyy-MM-dd'T'HH:mm")}
                onChange={(e) => setEndDate(new Date(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Object selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Wybrane SUW/Hydrofonownie ({selectedObjects.length} z {objects.length})
            </label>
            <div className="flex gap-2 mb-3">
              <button
                onClick={handleSelectAllObjects}
                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-200"
              >
                Zaznacz wszystkie
              </button>
              <button
                onClick={handleDeselectAllObjects}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200"
              >
                Odznacz wszystkie
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
              {objects.map(obj => (
                <label key={obj.id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                  <input
                    type="checkbox"
                    checked={selectedObjects.includes(obj.id)}
                    onChange={() => handleObjectToggle(obj.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{obj.name}</div>
                    <div className="text-xs text-gray-500">
                      {obj.infrastructure_type} • {obj.readings_count} odczytów
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      {selectedObjectsData.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          Wybierz przynajmniej jeden obiekt infrastruktury, aby wyświetlić wykresy
        </div>
      ) : (
        <div className="space-y-8">
          {selectedObjectsData.map(obj => (
            <StatisticsObjectChart
              key={obj.id}
              object={obj}
              readings={readingsByObject[obj.id] || []}
              readingTypeLabels={readingTypeLabels}
              viewMode={viewMode}
            />
          ))}
        </div>
      )}

      {/* Add Reading Modal */}
      <AddReadingModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        token={token}
        onReadingAdded={handleReadingAdded}
      />

      {/* Readings Management Table - tylko dla MASTER_ADMIN */}
      {isMasterAdmin && (
        <ReadingsManagementTable token={token} startDate={startDate} endDate={endDate} />
      )}
    </div>
  )
}
